/**
 * Tides — the one clean home for tidal heights and tidal streams, replacing the
 * old crowded Conditions tide block. Two tabs, three taps to anything:
 *
 *   HEIGHTS: pick a data source and a port, step the day forward or back (or jump
 *   to a date), read the day's HW/LW, drag along the curve for the height at any
 *   moment, and see the tidal levels underneath.
 *
 *   STREAMS: pick a source, turn the chart overlay on or off, and read the stream
 *   nearest the chart centre with a timeline to run it through the tide.
 *
 * TIMES. Everything on the Heights tab is shown in the PORT'S OWN standard time,
 * clearly labelled (UT, UT−1, …), because that is how paper charts, the almanac
 * and the RYA courses give tides. The old bug showed the HW/LW list in port-local
 * time while the curve was in UT, an hour out for the southern training ports;
 * now the list, the curve and every read-out agree on the one clock. The height
 * MODEL still works in real UTC underneath; only the labels are shifted.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Capacitor } from '@capacitor/core';
import { useChartPlotterStore } from '@/store';
import { useEnvStore } from '@/store/environment';
import { useUnitsStore, formatDepth, type DepthUnit } from '@/store/units';
import { useTideSourceStore } from '@/store/tideSources';
import { useTidePortsStore } from '@/store/tidePorts';
import { useFesStationsStore } from '@/store/fesStations';
import { useAdmiraltyStore } from '@/store/admiralty';
import { useChartSourcesStore } from '@/store/chartSources';
import { TIDE_HEIGHT_SOURCES, TIDE_STREAM_SOURCES } from '@/tides/tideSources';
import {
  HEIGHT_PORTS,
  nearestHeightPort,
  tideHeightState,
  heightAt as portHeightAt,
  type HeightPort,
  type TideHeightState,
} from '@/tides/heights';
import { TIDE_PLACES, resolveHeightSelection, type TidePlace } from '@/tides/tidePlaces';
import type { FesStation } from '@/tides/fes';
import { tidalLevelsForPort } from '@/tides/levels';
import { localDayWindow, findExtrema, eventsHeightAt } from '@/tides/curve';
import { predictHeightM } from '@/tides/fes';
import { nearestFesStation, fesHeightStateAt } from '@/tides/fesStations';
import { admiraltyHeightState } from '@/tides/admiraltyHeights';
import { admiraltyConfigured } from '@/tides/admiralty';
import {
  tidalStateAt,
  streamAtPosition,
  TIDAL_DIAMONDS,
  VICTORIA_DEFAULT_MS,
  VICTORIA_WINDOW,
} from '@/tides';
import { formatBearing, rhumbDistanceNm } from '@/nav';
import { Sheet } from './Sheet';
import { GlossaryTerm } from '@/help/GlossaryTerm';
import { TideCurve } from './TideCurve';

/** Fallback position when there's no fix and no chart centre (Sound of Mull). */
const TOBERMORY: [number, number] = [-6.064, 56.622];
const HOUR = 3_600_000;
const DAY = 86_400_000;
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "UT", "UT−1", "UT+2" — the label carried against every time on the tab. */
function zoneLabel(tzH: number): string {
  if (tzH === 0) return 'UT';
  return `UT${tzH > 0 ? '+' : '−'}${Math.abs(tzH)}`;
}
const pad2 = (n: number) => String(n).padStart(2, '0');
/** HH:MM of a real instant, read in the zone `tzMs` (ms east of UT). */
function fmtHM(ms: number, tzMs: number): string {
  const d = new Date(ms + tzMs);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}
/** "Sat 4 Jan" of a real instant in the port's zone. */
function fmtDay(ms: number, tzMs: number): string {
  const d = new Date(ms + tzMs);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}
function toDateInput(ms: number, tzMs: number): string {
  const d = new Date(ms + tzMs);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}
/** A yyyy-mm-dd (port-local) back to the real UTC ms of local midday that day. */
function fromDateInput(v: string, tzMs: number): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0) - tzMs;
}
/** A HH:MM (port-local) applied to the local day starting at `windowStart`. */
function timeOnDay(v: string, windowStart: number): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v);
  if (!m) return null;
  return windowStart + (Number(m[1]) * 60 + Number(m[2])) * 60_000;
}

// ---------------------------------------------------------------------------
// Heights tab
// ---------------------------------------------------------------------------

interface DayEvent {
  t: number;
  type: 'H' | 'L';
  heightM: number;
}

/** What the Heights tab needs from whichever source is active, one shape. */
interface HeightView {
  ok: boolean;
  /** Shown in place of the card when the source has nothing for here. */
  blockedNote?: string;
  portLabel: string;
  tzH: number;
  /** The source's "now" (the training scrubber clock, or the wall clock). */
  now: number;
  heightAt: (ms: number) => number;
  eventsIn: (a: number, b: number) => DayEvent[];
  state: TideHeightState | null;
  kindLabel: string;
  /** Training exposes a port picker; the real sources auto-pick the nearest. */
  picker: boolean;
  /** The FES station picker (nearest-sorted, favourites) instead of the training one. */
  fesPicker?: boolean;
  levels: ReactNode;
  footNote: string;
}

function trainingView(port: HeightPort, now: number, place: TidePlace | undefined, distNm: number | undefined, depthUnit: DepthUnit): HeightView {
  const l = tidalLevelsForPort(port);
  const levels =
    l.sampleSize > 0 ? (
      <div className="acp-levels">
        <div><span>MHWS</span><b>{formatDepth(l.mhwsM, depthUnit)}</b></div>
        <div><span>MHWN</span><b>{formatDepth(l.mhwnM, depthUnit)}</b></div>
        <div><span>MLWS</span><b>{formatDepth(l.mlwsM, depthUnit)}</b></div>
        <div><span>MLWN</span><b>{formatDepth(l.mlwnM, depthUnit)}</b></div>
        <div className="acp-level-soon"><span><GlossaryTerm termKey="hat">HAT</GlossaryTerm></span><b>Coming soon</b></div>
        <div><span>Mean level</span><b>{formatDepth(l.meanLevelM, depthUnit)}</b></div>
        <div><span>Spring range</span><b>{formatDepth(l.springRangeM, depthUnit)}</b></div>
        <div><span>Neap range</span><b>{formatDepth(l.neapRangeM, depthUnit)}</b></div>
      </div>
    ) : (
      <p className="acp-note">Not enough data to derive the tidal levels for this port.</p>
    );
  const distTxt = distNm != null ? ` (${distNm.toFixed(1)} nm)` : '';
  return {
    ok: true,
    portLabel: place ? place.name : port.name,
    tzH: port.tzH,
    now,
    heightAt: (ms) => {
      const h = portHeightAt(port, ms);
      return h == null ? NaN : h;
    },
    eventsIn: (a, b) => port.events.filter((e) => e.t >= a && e.t <= b).map((e) => ({ t: e.t, type: e.type, heightM: e.heightM })),
    state: tideHeightState(port, now),
    kindLabel: place
      ? `Marina · tides from ${port.name}${distTxt}, the nearest tide station`
      : port.secondaryOf
        ? `Secondary port · referred to ${port.secondaryOf}`
        : 'Standard port',
    picker: true,
    levels,
    footNote:
      (place
        ? `${place.name} has no tide station of its own, so these are ${port.name}'s tides, the nearest station. `
        : '') +
      'RYA training data: published HW/LW with the standard Admiralty tidal-curve method between them. Not for navigation.',
  };
}

// ---------------------------------------------------------------------------

function HeightsTab() {
  const heightSourceId = useTideSourceStore((s) => s.heightSourceId);
  const setHeightSource = useTideSourceStore((s) => s.setHeightSource);
  const admiraltyKey = useTideSourceStore((s) => s.admiraltyKey);
  const hasKey = admiraltyConfigured(admiraltyKey);

  const selectedPortId = useTidePortsStore((s) => s.selectedPortId);
  const setSelectedPortId = useTidePortsStore((s) => s.setSelectedPortId);
  const fesStationId = useTidePortsStore((s) => s.fesStationId);
  const setFesStationId = useTidePortsStore((s) => s.setFesStationId);
  const setHeightPortId = useEnvStore((s) => s.setHeightPortId);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const depthUnit = useUnitsStore((s) => s.depth);
  const ownPos = useChartPlotterStore((s) => s.ownship.position);
  const viewBounds = useChartSourcesStore((s) => s.viewBounds);

  const fesStations = useFesStationsStore((s) => s.stations);
  const admEvents = useAdmiraltyStore((s) => s.events);
  const admName = useAdmiraltyStore((s) => s.stationName);
  const admCountry = useAdmiraltyStore((s) => s.stationCountry);
  const admContinuous = useAdmiraltyStore((s) => s.stationContinuous);
  const admStatus = useAdmiraltyStore((s) => s.status);
  const admError = useAdmiraltyStore((s) => s.error);

  // Live Admiralty fetch when its source is active and a key is present.
  useEffect(() => {
    if (heightSourceId !== 'admiralty') return;
    const key = useTideSourceStore.getState().admiraltyKey;
    if (!admiraltyConfigured(key)) return;
    void useAdmiraltyStore.getState().load(key, ownPos ?? TOBERMORY);
  }, [heightSourceId, ownPos]);

  // dayAnchor null = follow the source's "now"; otherwise the chosen day.
  const [dayAnchor, setDayAnchor] = useState<number | null>(null);
  const [cursor, setCursor] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const view: HeightView = useMemo(() => {
    if (heightSourceId === 'fes') {
      // A manually chosen station wins; nearest-to-the-boat is the default.
      // The skipper planning tomorrow's leg wants the DESTINATION's tide, not
      // the tide where the boat sits tonight (fb-msmaupvz-0).
      const chosen = fesStationId ? fesStations.find((f) => f.id === fesStationId) ?? null : null;
      const st = chosen ?? nearestFesStation(fesStations, ownPos ?? TOBERMORY);
      if (!st) {
        return {
          ok: false,
          blockedNote:
            'FES2014 selected, but no FES station data is loaded yet. Add the FES files and run scripts/extract_fes.py, or switch to RYA training.',
          portLabel: 'No FES station', tzH: 0, now: Date.now(), heightAt: () => NaN,
          eventsIn: () => [], state: null, kindLabel: '', picker: false, levels: null, footNote: '',
        };
      }
      const now = Date.now();
      const heightAt = (ms: number) => predictHeightM(st, ms);
      return {
        ok: true, portLabel: st.name, tzH: 0, now, heightAt,
        eventsIn: (a, b) => findExtrema(heightAt, a, b).map((m) => ({ t: m.t, type: m.type, heightM: m.heightM })),
        state: fesHeightStateAt(st, now),
        kindLabel: chosen
          ? 'FES2014 harmonic station · chosen by hand'
          : 'FES2014 harmonic station · nearest to the boat',
        picker: false,
        fesPicker: true,
        levels: (
          <p className="acp-note">
            A harmonic model gives the curve and the HW/LW, but not the published MHWS/MHWN/MLWN/MLWS levels or
            HAT/LAT. Read those off the chart’s tidal-levels box for this area.
          </p>
        ),
        footNote: 'FES2014 harmonic prediction above chart datum, at the real time. Free and offline; coarse in narrow waters. Not for navigation.',
      };
    }
    if (heightSourceId === 'admiralty') {
      if (!hasKey) {
        return {
          ok: false,
          blockedNote: Capacitor.isNativePlatform()
            ? "Admiralty UK isn't reachable in the app (it needs the dev proxy). Pick FES2014 (free, offline) or RYA training."
            : 'Admiralty UK selected — add your API key in Settings to use it.',
          portLabel: 'Admiralty UK', tzH: 0, now: Date.now(), heightAt: () => NaN,
          eventsIn: () => [], state: null, kindLabel: '', picker: false, levels: null, footNote: '',
        };
      }
      if (admStatus === 'loading') {
        return { ok: false, blockedNote: 'Admiralty UK — fetching the nearest station…', portLabel: 'Admiralty UK', tzH: 0, now: Date.now(), heightAt: () => NaN, eventsIn: () => [], state: null, kindLabel: '', picker: false, levels: null, footNote: '' };
      }
      if (admStatus === 'error') {
        return { ok: false, blockedNote: `Admiralty UK — ${admError ?? 'fetch failed'}.`, portLabel: 'Admiralty UK', tzH: 0, now: Date.now(), heightAt: () => NaN, eventsIn: () => [], state: null, kindLabel: '', picker: false, levels: null, footNote: '' };
      }
      const marks = admEvents
        .map((e) => ({ t: Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(e.DateTime) ? e.DateTime : `${e.DateTime}Z`), type: e.EventType === 'HighWater' ? ('H' as const) : ('L' as const), heightM: e.Height }))
        .filter((m) => Number.isFinite(m.t))
        .sort((a, b) => a.t - b.t);
      if (marks.length < 2) {
        return { ok: false, blockedNote: 'Admiralty UK — no tidal events for this station yet.', portLabel: admName || 'Admiralty UK', tzH: 0, now: Date.now(), heightAt: () => NaN, eventsIn: () => [], state: null, kindLabel: '', picker: false, levels: null, footNote: '' };
      }
      const now = Date.now();
      const heightAt = (ms: number) => eventsHeightAt(marks, ms);
      const kind = admContinuous === false ? 'Secondary-type station' : admContinuous ? 'Standard-type station' : 'Admiralty station';
      return {
        ok: true, portLabel: `${admName}${admCountry ? `, ${admCountry}` : ''}`, tzH: 0, now, heightAt,
        eventsIn: (a, b) => marks.filter((m) => m.t >= a && m.t <= b),
        state: admiraltyHeightState(admEvents, now),
        kindLabel: `${kind} · nearest to the boat`,
        picker: false,
        levels: (
          <p className="acp-note">
            The free Admiralty Discovery tier gives HW/LW only, so MHWS/MHWN/MLWN/MLWS and HAT/LAT aren’t shown.
            The curve between the official HW and LW is a cosine fill (close to the rule of twelfths), not the port’s
            published curve.
          </p>
        ),
        footNote: 'Official Admiralty HW/LW (free Discovery tier), live. Curve interpolated between the events.',
      };
    }
    // training (default): the selection is a port id, or a marina that borrows
    // its nearest tide station.
    const sel = resolveHeightSelection(selectedPortId);
    return trainingView(sel.port, streamTimeMs, sel.place, sel.distNm, depthUnit);
  }, [heightSourceId, hasKey, fesStations, fesStationId, ownPos, admEvents, admName, admCountry, admContinuous, admStatus, admError, selectedPortId, streamTimeMs, depthUnit]);

  // Reset the day/cursor when the source or port changes, so we land on "now".
  useEffect(() => {
    setDayAnchor(null);
    setCursor(null);
    setPickerOpen(false);
  }, [heightSourceId, selectedPortId]);

  const tzMs = view.tzH * HOUR;
  const anchor = dayAnchor ?? view.now;
  const win = localDayWindow(anchor, tzMs);
  const zl = zoneLabel(view.tzH);
  const dayEvents = view.ok ? view.eventsIn(win.start, win.end) : [];
  const readMs = cursor ?? (view.now >= win.start && view.now <= win.end ? view.now : null);
  const readH = readMs != null ? view.heightAt(readMs) : null;
  // Where to measure port distances from: the boat's fix, else the middle of
  // the current chart view, else nothing (the picker then falls back to the
  // favourites-first, standard-before-secondary order).
  const pickerAnchor: [number, number] | null =
    ownPos ?? (viewBounds ? [(viewBounds[0] + viewBounds[2]) / 2, (viewBounds[1] + viewBounds[3]) / 2] : null);

  return (
    <>
      <label className="acp-fieldlabel">Source</label>
      <div className="acp-seg wrap">
        {TIDE_HEIGHT_SOURCES.map((s) => (
          <button key={s.id} type="button" className={heightSourceId === s.id ? 'on' : ''} onClick={() => setHeightSource(s.id)} title={s.note}>
            {s.name}{s.offline ? ' · offline' : ''}
          </button>
        ))}
      </div>

      {/* Location */}
      <label className="acp-fieldlabel">Location</label>
      {view.picker ? (
        <PortPicker
          open={pickerOpen}
          setOpen={setPickerOpen}
          anchor={pickerAnchor}
          selectedId={selectedPortId}
          selectedLabel={view.portLabel}
          onPick={(id) => {
            setSelectedPortId(id);
            setHeightPortId(resolveHeightSelection(id).port.id);
            setPickerOpen(false);
          }}
        />
      ) : view.fesPicker ? (
        <FesStationPicker
          open={pickerOpen}
          setOpen={setPickerOpen}
          anchor={pickerAnchor}
          stations={fesStations}
          selectedId={fesStationId}
          selectedLabel={view.portLabel}
          onPick={(id) => {
            setFesStationId(id);
            setPickerOpen(false);
          }}
        />
      ) : (
        <div className="acp-locbtn static">
          <span className="nm">{view.portLabel}</span>
          <span className="hint">nearest station</span>
        </div>
      )}

      {!view.ok ? (
        <p className="acp-note" style={{ marginTop: 10 }}>{view.blockedNote}</p>
      ) : (
        <>
          <p className="acp-note" style={{ marginTop: 4 }}>{view.kindLabel}</p>

          {/* Date controls */}
          <div className="acp-daterow">
            <button type="button" className="nav" aria-label="Previous day" onClick={() => { setDayAnchor((anchor) - DAY); setCursor(null); }}>‹</button>
            <div className="lbl">{fmtDay(anchor, tzMs)}</div>
            <button type="button" className="nav" aria-label="Next day" onClick={() => { setDayAnchor((anchor) + DAY); setCursor(null); }}>›</button>
            <label className="cal" title="Pick a date" aria-label="Pick a date">
              📅
              <input type="date" value={toDateInput(anchor, tzMs)} onChange={(e) => { const ms = fromDateInput(e.target.value, tzMs); if (ms != null) { setDayAnchor(ms); setCursor(null); } }} />
            </label>
            <button type="button" className="now" aria-label="Go to now" onClick={() => { setDayAnchor(null); setCursor(null); }}>Now</button>
          </div>

          {/* HW/LW for the day */}
          <div className="acp-hwlw">
            {dayEvents.length === 0 ? (
              <p className="acp-note">No HW/LW fall on this day.</p>
            ) : (
              dayEvents.map((e, i) => (
                <div key={i} className="row">
                  <span className={`tag ${e.type === 'H' ? 'hw' : 'lw'}`}>{e.type === 'H' ? 'HW' : 'LW'}</span>
                  <span className="tm">{fmtHM(e.t, tzMs)} {zl}</span>
                  <span className="ht">{formatDepth(e.heightM, depthUnit)}</span>
                </div>
              ))
            )}
          </div>

          {/* Now / cursor read-out */}
          {view.state && (
            <div className="acp-kv">
              <span className="k">Height {cursor == null ? 'now' : 'at cursor'}</span>
              <span className="v">
                {formatDepth(readH != null && Number.isFinite(readH) ? readH : view.state.heightM, depthUnit)}
                {cursor == null && <> · {view.state.rising ? 'rising ▲' : 'falling ▼'} · {view.state.springLabel}</>}
              </span>
            </div>
          )}

          {/* Curve with a clock button to centre on now + a time box */}
          <div className="acp-curvehdr">
            <button type="button" className="clockbtn" aria-label="Centre on now" title="Centre on now" onClick={() => { setDayAnchor(null); setCursor(null); }}>🕑</button>
            <span className="ttl">Tidal curve · {fmtDay(anchor, tzMs)} · times {zl}</span>
          </div>
          <TideCurve heightAt={view.heightAt} start={win.start} end={win.end} nowMs={view.now} tzOffsetMs={tzMs} zoneLabel={zl} cursorMs={cursor} onScrub={setCursor} />
          <div className="acp-timebox">
            <label>Time
              <input type="time" value={readMs != null ? fmtHM(readMs, tzMs) : ''} onChange={(e) => { const ms = timeOnDay(e.target.value, win.start); if (ms != null) setCursor(ms); }} />
            </label>
            <span className="hint">drag the curve or enter a time</span>
          </div>

          {/* Levels + spring/neap + today's range */}
          <label className="acp-fieldlabel" style={{ marginTop: 12 }}>Tidal levels</label>
          {view.state && (
            <p className="acp-note" style={{ marginTop: 0 }}>
              Today: {view.state.springLabel.toLowerCase()}, range {formatDepth(Math.abs(view.state.next.heightM - view.state.prev.heightM), depthUnit)}
              (this HW {formatDepth(Math.max(view.state.prev.heightM, view.state.next.heightM), depthUnit)} to LW {formatDepth(Math.min(view.state.prev.heightM, view.state.next.heightM), depthUnit)}).
            </p>
          )}
          {view.levels}
          <p className="acp-note">{view.footNote}</p>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Port picker: search + favourites-first list, a star on each row.
// ---------------------------------------------------------------------------

/**
 * The FES station picker: the same tap-to-change, search, nearest-first and
 * starred-favourites pattern as the training ports, over the real FES station
 * list (fb-msmaupvz-0). "Follow the boat" at the top puts it back on automatic
 * nearest-station, which stays the default.
 */
function FesStationPicker({
  open,
  setOpen,
  anchor,
  stations,
  selectedId,
  selectedLabel,
  onPick,
}: {
  open: boolean;
  setOpen: (b: boolean) => void;
  anchor: [number, number] | null;
  stations: FesStation[];
  selectedId: string | null;
  selectedLabel: string;
  onPick: (id: string | null) => void;
}) {
  const favourites = useTidePortsStore((s) => s.favourites);
  const toggleFavourite = useTidePortsStore((s) => s.toggleFavourite);
  const [q, setQ] = useState('');

  const ordered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = stations.filter((st) => st.name.toLowerCase().includes(needle));
    const favs = list.filter((st) => favourites.includes(st.id));
    const rest = list.filter((st) => !favourites.includes(st.id));
    const byDist = (xs: FesStation[]) =>
      anchor ? [...xs].sort((x, y) => rhumbDistanceNm(anchor, x.position) - rhumbDistanceNm(anchor, y.position)) : xs;
    return [...byDist(favs), ...byDist(rest)];
  }, [stations, favourites, q, anchor]);

  if (!open) {
    return (
      <button type="button" className="acp-locbtn" aria-expanded={false} onClick={() => setOpen(true)}>
        <span className="nm">{selectedLabel}</span>
        <span className="hint">{selectedId ? 'chosen · tap to change ▾' : 'nearest · tap to change ▾'}</span>
      </button>
    );
  }
  return (
    <div className="acp-portpick">
      <input
        className="acp-portsearch"
        type="search"
        placeholder="Search stations…"
        value={q}
        autoFocus
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search tide stations"
      />
      <div className="acp-portlist">
        <div className={`acp-portrow${selectedId === null ? ' sel' : ''}`}>
          <button type="button" className="pick" onClick={() => onPick(null)}>
            <span className="nm">Follow the boat</span>
            <span className="sub">always the nearest station to our position</span>
          </button>
        </div>
        {ordered.length === 0 && <p className="acp-note">No station matches “{q}”.</p>}
        {ordered.map((st) => {
          const fav = favourites.includes(st.id);
          const dist = anchor ? ` · ${rhumbDistanceNm(anchor, st.position).toFixed(1)} NM` : '';
          return (
            <div key={st.id} className={`acp-portrow${st.id === selectedId ? ' sel' : ''}`}>
              <button type="button" className="pick" onClick={() => onPick(st.id)}>
                <span className="nm">{st.name}</span>
                <span className="sub">FES2014 harmonic{dist}</span>
              </button>
              <button
                type="button"
                className={`acp-star${fav ? ' on' : ''}`}
                aria-pressed={fav}
                aria-label={fav ? `Unfavourite ${st.name}` : `Favourite ${st.name}`}
                onClick={() => toggleFavourite(st.id)}
              >
                {fav ? '★' : '☆'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PortPicker({
  open,
  setOpen,
  anchor,
  selectedId,
  selectedLabel,
  onPick,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  /** Where to measure port distances from (boat, else chart centre), or null. */
  anchor: [number, number] | null;
  selectedId: string;
  selectedLabel: string;
  onPick: (id: string) => void;
}) {
  const favourites = useTidePortsStore((s) => s.favourites);
  const toggleFavourite = useTidePortsStore((s) => s.toggleFavourite);
  const [q, setQ] = useState('');

  const ordered = useMemo(() => {
    const favSet = new Set(favourites);
    const match = (p: HeightPort) => p.name.toLowerCase().includes(q.trim().toLowerCase());
    const list = HEIGHT_PORTS.filter(match);
    // Favourites first (in favourite order). Then the rest: nearest-first when we
    // have somewhere to measure from (the boat's fix or the chart centre), so the
    // port we're next to floats to the top, matching how the real position-based
    // sources auto-pick the nearest station. Ports carry charted positions now, so
    // this is an honest proximity sort. With no anchor, or for a port with no
    // position, we keep the favourites-first, standard-before-secondary order.
    const favs = favourites.map((id) => list.find((p) => p.id === id)).filter((p): p is HeightPort => !!p);
    const rest = list.filter((p) => !favSet.has(p.id));
    if (anchor) {
      const a = anchor;
      const withPos: HeightPort[] = [];
      const noPos: HeightPort[] = [];
      for (const p of rest) (p.position ? withPos : noPos).push(p);
      withPos.sort((x, y) => {
        const px = x.position;
        const py = y.position;
        if (!px || !py) return 0;
        return rhumbDistanceNm(a, px) - rhumbDistanceNm(a, py);
      });
      return [...favs, ...withPos, ...noPos];
    }
    return [...favs, ...rest];
  }, [favourites, q, anchor]);

  const distLabel = (p: HeightPort): string | null => {
    const pos = p.position;
    if (!anchor || !pos) return null;
    return `${rhumbDistanceNm(anchor, pos).toFixed(1)} NM`;
  };

  // Marinas / yacht havens from the training chart. They have no tides of their
  // own, so they're shown as a separate group and read their tides off the
  // nearest station. Same proximity sort as the ports when there's an anchor.
  const orderedPlaces = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = TIDE_PLACES.filter((pl) => pl.name.toLowerCase().includes(needle));
    if (anchor) {
      const a = anchor;
      return [...list].sort((x, y) => rhumbDistanceNm(a, x.position) - rhumbDistanceNm(a, y.position));
    }
    return list;
  }, [q, anchor]);

  const placeSub = (pl: TidePlace): string => {
    const n = nearestHeightPort(pl.position);
    const dist = anchor ? ` · ${rhumbDistanceNm(anchor, pl.position).toFixed(1)} NM` : '';
    return n ? `marina · tides from ${n.port.name}${dist}` : `marina${dist}`;
  };

  if (!open) {
    return (
      <button type="button" className="acp-locbtn" aria-expanded={false} onClick={() => setOpen(true)}>
        <span className="nm">{selectedLabel}</span>
        <span className="hint">tap to change ▾</span>
      </button>
    );
  }
  return (
    <div className="acp-portpick">
      <input className="acp-portsearch" type="search" placeholder="Search ports & marinas…" value={q} autoFocus onChange={(e) => setQ(e.target.value)} aria-label="Search ports" />
      <div className="acp-portlist">
        {ordered.length === 0 && orderedPlaces.length === 0 && (
          <p className="acp-note">No port or marina matches “{q}”.</p>
        )}
        {ordered.map((p) => {
          const fav = favourites.includes(p.id);
          return (
            <div key={p.id} className={`acp-portrow${p.id === selectedId ? ' sel' : ''}`}>
              <button type="button" className="pick" onClick={() => onPick(p.id)}>
                <span className="nm">{p.name}</span>
                <span className="sub">
                  {p.secondaryOf ? `secondary · ${p.secondaryOf}` : 'standard'}
                  {distLabel(p) ? ` · ${distLabel(p)}` : ''}
                </span>
              </button>
              <button type="button" className={`acp-star${fav ? ' on' : ''}`} aria-pressed={fav} aria-label={fav ? `Unfavourite ${p.name}` : `Favourite ${p.name}`} onClick={() => toggleFavourite(p.id)}>
                {fav ? '★' : '☆'}
              </button>
            </div>
          );
        })}
        {orderedPlaces.length > 0 && (
          <div className="acp-portgroup">Marinas &amp; yacht havens · tides from the nearest port</div>
        )}
        {orderedPlaces.map((pl) => (
          <div key={pl.id} className={`acp-portrow${pl.id === selectedId ? ' sel' : ''}`}>
            <button type="button" className="pick" onClick={() => onPick(pl.id)}>
              <span className="nm">{pl.name}</span>
              <span className="sub">{placeSub(pl)}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Streams tab
// ---------------------------------------------------------------------------

function StreamsTab() {
  const streamSourceId = useTideSourceStore((s) => s.streamSourceId);
  const setStreamSource = useTideSourceStore((s) => s.setStreamSource);
  const admiraltyKey = useTideSourceStore((s) => s.admiraltyKey);
  const hasKey = admiraltyConfigured(admiraltyKey);

  const streamOn = useChartPlotterStore((s) => !!s.groupVisibility['tidal-stream']);
  const toggleGroup = useChartPlotterStore((s) => s.toggleGroup);
  const ownPos = useChartPlotterStore((s) => s.ownship.position);
  const viewBounds = useChartSourcesStore((s) => s.viewBounds);

  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const setStreamTimeMs = useEnvStore((s) => s.setStreamTimeMs);
  const setOverlayMode = useEnvStore((s) => s.setOverlayMode);
  const openScrubber = useEnvStore((s) => s.openScrubber);

  // "Nearest to where the chart is centred": the middle of the current view, or
  // the boat, or the training-area fallback.
  const centre: [number, number] = viewBounds
    ? [(viewBounds[0] + viewBounds[2]) / 2, (viewBounds[1] + viewBounds[3]) / 2]
    : ownPos ?? TOBERMORY;

  const st = tidalStateAt(streamTimeMs);
  const [cx, cy] = centre;
  const nearest = useMemo(() => {
    let best = TIDAL_DIAMONDS[0];
    let bestD = Infinity;
    for (const d of TIDAL_DIAMONDS) {
      const dx = d.position[0] - cx;
      const dy = d.position[1] - cy;
      const dd = dx * dx + dy * dy;
      if (dd < bestD) { bestD = dd; best = d; }
    }
    return best;
  }, [cx, cy]);
  const sample = streamAtPosition(centre, streamTimeMs);
  const h = st.hoursFromHW;
  const hwLabel = Math.abs(h) < 0.05 ? 'HW Victoria' : `HW Victoria ${h > 0 ? '+' : '−'}${Math.abs(h).toFixed(1)} h`;
  const sn = st.springRatio >= 0.85 ? 'Springs' : st.springRatio <= 0.15 ? 'Neaps' : 'Springs–neaps';

  // The chart-diamond family is the only one wired to real readings here; the
  // others are honest about having no data for this fictional area yet.
  const familyHasData = streamSourceId === 'training';
  const sourceNote =
    streamSourceId === 'fes'
      ? 'FES2014 currents: free and offline, but coarse. An indication only in narrow waters, and not yet wired to the chart arrows, so the readout below still uses the training diamonds.'
      : streamSourceId === 'authored'
        ? 'Authored diamonds: real transcribed chart diamonds, added per area when the atlas is supplied. None for this area yet, so the readout below uses the training diamonds.'
        : streamSourceId === 'admiralty'
          ? hasKey
            ? 'Admiralty streams (official, paid Foundation tier): live fetch is the next wiring step, so the readout below still uses the training diamonds.'
            : 'Admiralty streams need an Admiralty Foundation (paid) key in Settings. The readout below uses the training diamonds meanwhile.'
          : 'RYA training diamonds for the fictional area, referred to HW Victoria. Not for navigation.';

  const atNow = streamTimeMs === VICTORIA_DEFAULT_MS;

  return (
    <>
      <label className="acp-fieldlabel">Source</label>
      <div className="acp-seg wrap">
        {TIDE_STREAM_SOURCES.map((s) => (
          <button key={s.id} type="button" className={streamSourceId === s.id ? 'on' : ''} onClick={() => setStreamSource(s.id)} title={s.note}>
            {s.name}{s.offline ? ' · offline' : ''}
          </button>
        ))}
      </div>
      <p className="acp-note">{sourceNote}</p>

      {/* Overlay on/off */}
      <div className="acp-layer" style={{ marginTop: 6 }}>
        <div className="lt">
          <b>Show streams on the chart</b>
          <span>Arrows over the chart, stepping with the timeline below.</span>
        </div>
        <button
          type="button"
          className={`acp-sw${streamOn ? ' on' : ''}`}
          aria-pressed={streamOn}
          aria-label="Tidal stream overlay"
          onClick={() => { setOverlayMode('streams'); toggleGroup('tidal-stream'); }}
        />
      </div>

      {/* Nearest stream to the chart centre */}
      <label className="acp-fieldlabel" style={{ marginTop: 12 }}>Nearest stream to the chart centre</label>
      <p className="acp-note" style={{ marginTop: 0 }}>
        Diamond {nearest.letter} (chart {nearest.chart.toUpperCase()}) · {hwLabel} · <GlossaryTerm termKey="springsneaps">{sn}</GlossaryTerm>
        {!familyHasData ? ' · from the training diamonds' : ''}
      </p>
      <div className="acp-kv"><span className="k">Set now</span><span className="v">{formatBearing(sample.setDeg)}</span></div>
      <div className="acp-kv"><span className="k">Rate now</span><span className="v">{sample.rateKn.toFixed(1)} kn</span></div>

      {/* Timeline to run the stream through the tide */}
      <label className="acp-fieldlabel" style={{ marginTop: 12 }}>Timeline · {atNow ? 'now' : `${fmtHM(streamTimeMs, 0)} UT`}</label>
      <div className="acp-streamtl">
        <button type="button" aria-label="Back one hour" onClick={() => setStreamTimeMs(streamTimeMs - HOUR)}>−1h</button>
        <input type="range" min={VICTORIA_WINDOW.startMs} max={VICTORIA_WINDOW.endMs} step={1_800_000} value={streamTimeMs} aria-label="Stream time" onChange={(e) => setStreamTimeMs(Number(e.target.value))} />
        <button type="button" aria-label="Forward one hour" onClick={() => setStreamTimeMs(streamTimeMs + HOUR)}>+1h</button>
        <button type="button" className="reset" aria-label="Back to now" onClick={() => setStreamTimeMs(VICTORIA_DEFAULT_MS)}>Now</button>
      </div>

      {/* On-chart time controls: only meaningful once the overlay is on */}
      {streamOn && (
        <div className="acp-btn-row" style={{ marginTop: 10 }}>
          <button type="button" className="acp-btn full" onClick={() => { setOverlayMode('streams'); openScrubber(); }}>
            Show time controls on the chart
          </button>
        </div>
      )}
      <p className="acp-note">
        Springs and neaps come from the day’s range against Victoria’s mean ranges, so the streams and the heights
        agree on what counts as springs. Not for navigation.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------

export function TidesSheet() {
  const [tab, setTab] = useState<'heights' | 'streams'>('heights');

  // Keep the live env port (read by the tide data box and the feature sheet) in
  // step with the persisted selection, so a restart reopens on the same port.
  const selectedPortId = useTidePortsStore((s) => s.selectedPortId);
  const heightPortId = useEnvStore((s) => s.heightPortId);
  const setHeightPortId = useEnvStore((s) => s.setHeightPortId);
  useEffect(() => {
    // A marina selection resolves to its nearest station; keep the env's live
    // port a real station id so the tide box and feature sheet read real data.
    const stationId = resolveHeightSelection(selectedPortId).port.id;
    if (heightPortId !== stationId) setHeightPortId(stationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortId]);

  return (
    <Sheet name="tides" title="Tides" sub="Heights and streams in one place. Everything here is training data unless a real source is picked. Not for navigation.">
      <div className="acp-tabs">
        <button type="button" className={tab === 'heights' ? 'on' : ''} onClick={() => setTab('heights')}>Heights</button>
        <button type="button" className={tab === 'streams' ? 'on' : ''} onClick={() => setTab('streams')}>Streams</button>
      </div>
      {tab === 'heights' ? <HeightsTab /> : <StreamsTab />}
    </Sheet>
  );
}
